import { useDisclosure } from '@mantine/hooks';
import { useEffect, useRef, useState, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from 'utils/logger';
import { getClientLabel, getCompany, getUserAvatarColour, getUserInitials, parseSampleOtbMoment } from 'utils/helper';
import DateDisplay from 'core/components/dateDisplay/DateDisplay';
import PaletteInfo from '../colorSwatch/PaletteInfo';
import { getSyleColors } from 'services/styleService';
import ColorSwatch from '../colorSwatch/ColorSwatch';

const { Popover, Text, Group, Button, createStyles, Box, Badge, Avatar, Indicator, Image, Stack, Divider, Skeleton, ScrollArea } = require('@mantine/core');

const useStyles = createStyles((theme) => ({
    card: {
        //   margin:-theme.spacing.md,
        padding: 0,
        position: 'relative',
    },

    imageSection: {
        padding: theme.spacing.md,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
    },

    label: {
        marginBottom: theme.spacing.xs,
        lineHeight: 1,
        fontWeight: 700,
        fontSize: theme.fontSizes.xs,
        //   letterSpacing: -0.25),
        textTransform: 'uppercase',
    },

    section: {
        paddingTop: theme.spacing.md,
        paddingBottom: theme.spacing.md,
        // borderTop: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        margin: 0,
    },

    icon: {
        color: theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[5],
    },
}));

// Memoized PaletteInfo component to prevent unnecessary re-renders
const MemoizedPaletteInfo = memo(({ paletteData, layout, size, showColorValues, showScanInfo }) => (
    <Box w='100%'>
        <PaletteInfo 
            paletteData={paletteData} 
            layout={layout} 
            size={size} 
            showColorValues={showColorValues} 
            showScanInfo={showScanInfo} 
        />
    </Box>
));

const PalettePopover = memo((props) => {
    const [opened, { close, open }] = useDisclosure(false);
    const [data, setData] = useState();
    const { classes } = useStyles();
    const popRef = useRef();
    const doClose = useRef(true);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const cardRef = useRef();
    const timeoutRef = useRef();
    
    // Memoize expensive calculations
    const userInitials = useMemo(() => {
        return data?.operator ? getUserInitials(data.operator.split(' ')[0], data.operator.split(' ')[1] || '') : '';
    }, [data?.operator]);

    const themeCol = useMemo(() => {
        return getUserAvatarColour(userInitials);
    }, [userInitials]);

    const hasColors = useMemo(() => {
        return data?.colours && data.colours.length > 0;
    }, [data?.colours]);

    // Memoize the fetch function to prevent unnecessary re-creations
    const fetchColors = useCallback(async (styleId) => {
        setLoading(true);
        try {
            const styleColors = await getSyleColors(styleId);
            logger.log(styleColors, 'styleColors');

            setData({ 
                styleId, 
                crSort: styleColors && styleColors.length ? styleColors[0].crSort : '', 
                colours: styleColors 
            });
        } catch (error) {
            console.error('Error fetching style colors:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (opened) {
            logger.log(props.data, data);

            if (!data || (data && data.styleId !== props.styleId)) {
                fetchColors(props.styleId);
            }
        }
    }, [opened, props.styleId, data, fetchColors]);

    // Close popover if colors become empty after data is loaded
    useEffect(() => {
        if (opened && !loading && data && !hasColors) {
            close();
        }
    }, [opened, loading, data, hasColors, close]);

    // Memoize event handlers to prevent unnecessary re-renders
    const mouseHandler = useCallback((e) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
            logger.log('mouseHandler', doClose.current, e.type);
            if (doClose.current) {
                close();
                doClose.current = true;
            }
        }, 200);
    }, [close]);

    const showDetails = useCallback(() => {
        if (props.data?.crPart) {
            // Navigate to color/palette details if available
            navigate(`/warehouse/colors/${encodeURIComponent(props.data.crPart)}`);
        } else if (props.data?.taskPart) {
            navigate(`/sessions/tasks/${encodeURIComponent(props.data.taskPart)}_${props.data.taskSort}`);
        }
        if (props.onShowDetails) {
            props.onShowDetails();
        }
    }, [props.data, props.onShowDetails, navigate]);

    const onButtonClick = useCallback(() => {
        if (!data?.colours) return;
        
        const cssContent = data.colours.map((colour) => `${colour.hex} {color: rgb(${colour.r},${colour.g},${colour.b});}`).join('\n');
        const blob = new Blob([cssContent], { type: 'text/css' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${data.styleId}.css`;

        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }, [data?.colours, data?.styleId]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // Memoize mouse event handlers
    const handleMouseEnter = useCallback(() => {
        // Always allow opening - either to show colors or to trigger data fetch
        open();
    }, [open]);

    const handleMouseLeave = useCallback((e) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
            if (doClose.current) {
                mouseHandler(e);
            }
        }, 1000);
    }, [mouseHandler]);

    const handleCardMouseLeave = useCallback((e) => {
        logger.log('onMouseLeave', doClose.current);
        doClose.current = true;
        if (doClose.current) {
            close();
            doClose.current = true;
        }
    }, [close]);

    const handleCardMouseEnter = useCallback(() => {
        open();
        doClose.current = false;
    }, [open]);

    return (
        <Popover withinPortal={true} width={600} position={props.position || 'bottom-end'} shadow='md' opened={opened}>
            <Popover.Target>
                <div
                    ref={popRef}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}>
                    {props.children}
                </div>
            </Popover.Target>

            <Popover.Dropdown p={'md'} sx={{ pointerEvents: 'initial' }}>
                <Box
                    ref={cardRef}
                    className={classes.card}
                    style={{ padding: 0 }}
                    onMouseLeave={handleCardMouseLeave}
                    onMouseEnter={handleCardMouseEnter}>
                    {loading ? (
                        <Box p='md'>
                            <Text size={'sm'} color='dimmed'>
                                Loading palette information...
                            </Text>
                            <Skeleton border={0} height={300} width={'100%'} />
                        </Box>
                    ) : (
                        <>
                            {data && data.colours && data.colours.length > 0 ? (
                                <div>
                                    <ColorSwatch colors={data.colours} autoWidth={true} />
                                    {/* Palette Information */}
                                    {hasColors && (
                                        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                                            <Group w='100%' mt='md'>
                                                {data.colours.map((colour, index) => (
                                                    <MemoizedPaletteInfo 
                                                        key={`${colour.crPart || colour.crSort || index}-${index}`} 
                                                        paletteData={colour} 
                                                        layout='horizontal' 
                                                        size='lg' 
                                                        showColorValues={true} 
                                                        showScanInfo={true} 
                                                    />
                                                ))}
                                            </Group>
                                        </div>
                                    )}

                                    {/* Additional Details if available */}
                                    {(data.style || data.otbDate || data.items) && (
                                        <>
                                            <Divider />
                                            <Box className={classes.section}>
                                                <Group mt={'sm'} position='apart'>
                                                    {data.style && (
                                                        <Box>
                                                            <Text size={'xs'} color='dimmed'>
                                                                Style
                                                            </Text>
                                                            <Text size={'sm'}>{data.style}</Text>
                                                        </Box>
                                                    )}
                                                    {data.otbDate && (
                                                        <Box>
                                                            <Text size={'xs'} color='dimmed'>
                                                                {getClientLabel(props.company, 'otbDate')}
                                                            </Text>
                                                            <Text size={'sm'}>{parseSampleOtbMoment(data.otbDate) ? <DateDisplay value={parseSampleOtbMoment(data.otbDate)} /> : '-'}</Text>
                                                        </Box>
                                                    )}
                                                    {data.items && (
                                                        <Box>
                                                            <Text size={'xs'} color='dimmed'>
                                                                Items
                                                            </Text>
                                                            <Text size={'sm'}>{data.items.length}</Text>
                                                        </Box>
                                                    )}
                                                </Group>
                                            </Box>
                                        </>
                                    )}

                                    {/* Action Button */}
                                    <Box className={classes.section} pb={0}>
                                        <Group spacing={30}>
                                            <Button variant='outline' style={{ flex: 1 }} onClick={onButtonClick}>
                                                Downnload Photoshop ready CSS
                                            </Button>
                                        </Group>
                                    </Box>
                                </div>
                            ) : (
                                <Box p='md'>
                                    <Text size={'sm'} color='dimmed'>
                                        There is no palette information available
                                    </Text>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Popover.Dropdown>
        </Popover>
    );
});

export default PalettePopover;

